import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { HeroChicago } from "@/components/site/HeroChicago";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { ValueProps } from "@/components/site/ValueProps";
import { Footer } from "@/components/site/Footer";
import { WhyChooseChicago } from "@/components/site/WhyChooseChicago";
import eddieOlczyk from "@/assets/restore/eddie-olczyk.png.asset.json";
import { doctors } from "@/lib/site-data";
import { safeJsonLd } from "@/lib/structured-data";
import { getChicagoStructuredData } from "@/lib/structured-data/chicago";
import { useLocalQuoteLinks } from "@/hooks/use-local-quote-links";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const NoShaveFuture = lazy(() => import("@/components/site/NoShaveFuture").then(m => ({ default: m.NoShaveFuture })));
const NoShaveWhy = lazy(() => import("@/components/site/NoShaveWhy").then(m => ({ default: m.NoShaveWhy })));
const Guarantee = lazy(() => import("@/components/site/Guarantee").then(m => ({ default: m.Guarantee })));
const Pricing = lazy(() => import("@/components/site/Pricing").then(m => ({ default: m.Pricing })));
const DoctorBio = lazy(() => import("@/components/site/DoctorBio").then(m => ({ default: m.DoctorBio })));
const Surgeons = lazy(() => import("@/components/site/Surgeons").then(m => ({ default: m.Surgeons })));
const DoctorsGrid = lazy(() => import("@/components/site/DoctorsGrid").then(m => ({ default: m.DoctorsGrid })));
const LocationsMapChicago = lazy(() => import("@/components/site/LocationsMapChicago").then(m => ({ default: m.LocationsMapChicago })));
const VirtualConsult = lazy(() => import("@/components/site/VirtualConsult").then(m => ({ default: m.VirtualConsult })));
const Faq = lazy(() => import("@/components/site/Faq").then(m => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/locations/restore-chicago")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/restore-chicago/" }],
    meta: [
      { title: "Get Your Hair Back | RESTORE® Hair Transplants — Chicago" },
      {
        name: "description",
        content:
          "Restore your confidence with the top hair restoration team serving Chicago and the surrounding suburbs. Over 20,000 procedures.",
      },
      { property: "og:title", content: "Get Your Hair Back | RESTORE® Hair Transplants — Chicago" },
      {
        property: "og:description",
        content:
          "RESTORE® Hair transplants in Chicago and the surrounding suburbs. Trusted by Chicago athletes and broadcasters for natural, undetectable results.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd({ "@graph": getChicagoStructuredData() }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  useLocalQuoteLinks();
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <HeroChicago />
        <NorwoodQuiz />
        <WhyChooseChicago />
        <Suspense fallback={null}>
          <div className="hidden md:block">
            <Results />
          </div>
          <ValueProps hideEyebrowMobile />
          <NoShaveFuture />
          <NoShaveWhy dougLeft />
          <Reviews />
          <Guarantee />
          <Pricing imageSrc={eddieOlczyk.url} imageAlt="Eddie Olczyk, U.S. Hockey Hall of Fame & RESTORE Client" hidePlayOverlay />
          <DoctorBio />
          <Surgeons eyebrow="RESTORE Chicago Hair Transplant Doctors" heading={<>A World Renowned <span className="text-foreground">Team</span></>} />
          <DoctorsGrid doctors={doctors.filter((d) => ["Dr. James Harris", "Dr. Adam Cloninger"].includes(d.name))} />
          <LocationsMapChicago />
          <Faq />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}